/**
 * ops 依頼の差戻し（依頼者へ追加情報を求める）の理由カタログと記録の共有型。
 *
 * 理由は集計（サービス別の差戻し理由内訳）に使うので自由記述ではなく固定の 4 択にし、依頼者へ送る
 * メッセージは理由とは別に原文のまま残す。product-api（検証・保存・BQ append）と PIL（モーダル・履歴表示）が
 * 同じ値・同じ上限を参照する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼の差戻し理由（情報不足 / 内容不明確 / 再現・確認不可 / WFID 必要）と差戻し記録（日時・操作者・理由・依頼者向けメッセージ）の単一 source。サービス別の差戻し理由集計の語彙
 * @graph-connects none
 */
/**
 * 差戻し理由の値（API / Firestore / BQ に保存される識別子）。並びはモーダルの表示順。
 * 値を変えると保存済みデータと BQ の集計が割れるため、追加はできても改名・削除はしない。
 * @graph-connects none
 */
export const OPS_SEND_BACK_REASONS = [
    'missing_info',
    'unclear',
    'cannot_confirm',
    'need_wfid',
];
/**
 * 差戻し理由の日本語ラベル。活動ログ本文のように **保存される文言** や、依頼者へ送る Slack 差戻し返信の
 * 理由ラベル（product-api）で使うため UI の i18n ではなく
 * ここを単一 source にする（i18n の ja 訳と同じ語彙にそろえる。`OPS_STATE_LABELS` と同じ扱い）。
 * @graph-connects none
 */
export const OPS_SEND_BACK_REASON_LABELS = {
    missing_info: '情報不足',
    unclear: '内容不明確',
    cannot_confirm: '再現・確認不可',
    need_wfid: 'WFID 必要',
};
/** 依頼者向けメッセージの最大文字数（API schema と PIL の textarea が共有）。 @graph-connects none */
export const OPS_SEND_BACK_MESSAGE_MAX = 2000;
/**
 * 1 依頼に保持する差戻し記録の上限。超えたら古い順に捨てる（Firestore 1 ドキュメントのサイズ上限を
 * メッセージ全文の蓄積で踏まないため）。件数・理由の集計は BQ append 行の `send_back_reason` で数えるが、
 * その行は差戻しの瞬間に 1 回だけ書かれ後続の書き込みで再送されないため、BQ insert が失敗した回は
 * ここ（Firestore）が唯一の記録になる。上限で捨てたぶんと insert 失敗が重なった回は復元できない。
 * @graph-connects none
 */
export const OPS_SEND_BACK_HISTORY_LIMIT = 20;
/**
 * 値が差戻し理由かを判定する（Firestore から読んだ未検証値の絞り込み用）。
 * @graph-connects none
 */
export function isOpsSendBackReason(value) {
    return typeof value === 'string' && OPS_SEND_BACK_REASONS.includes(value);
}
/**
 * Firestore から読んだ未検証の差戻し記録を、形が正しいものだけに絞って返す（旧ドキュメント / 未設定は空配列）。
 * 理由が未知の値の記録は落とす（画面に生の i18n キーが出る・集計語彙が割れるのを読み手側で防ぐ）。
 * product-api の DTO 写像と customer-success MCP の直読写像が共有する。
 * @graph-connects isOpsSendBackReason [calls] 理由が既知の値かを判定
 */
export function readOpsSendBacks(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((entry) => typeof entry === 'object' &&
        entry !== null &&
        typeof entry.at === 'string' &&
        typeof entry.message === 'string' &&
        (entry.actor === null || typeof entry.actor === 'string') &&
        isOpsSendBackReason(entry.reason));
}
/**
 * 既存の差戻し記録に 1 件追加し、上限を超えた古い記録を捨てた新しい配列を返す（入力は変更しない）。
 * @graph-connects none
 */
export function appendOpsSendBack(history, entry) {
    return [...(history ?? []), entry].slice(-OPS_SEND_BACK_HISTORY_LIMIT);
}
//# sourceMappingURL=send-back.js.map